import type { CiTestResult } from "@/lib/types";

// A restore-performance run (`bench cryod perf run`) publishes one row per
// backend × locality; the cell's stats ride the row's extra tags. This turns
// those rows into the table bench prints: backend | loc | n | p50 | p95 | p99 | variance.

export const PERF_BACKEND_ORDER = ["donor", "dram", "tiered_nfs", "tiered_s3"] as const;
export const PERF_BACKEND_LABELS: Record<string, string> = {
  donor: "Donor",
  dram: "DRAM",
  tiered_nfs: "Tiered NFS",
  tiered_s3: "Tiered S3",
};
const LOCALITY_ORDER = ["same", "cross"] as const;

export interface PerfRow {
  resultId: number;
  backend: string;
  locality: string | null; // null = the whole backend phase failed before any sample
  n: number;
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
  variancePct: number | null;
  tierMismatch: number;
  status: string;
  error: string | null;
}

function numberTag(tags: Record<string, string>, key: string): number | null {
  const raw = tags[key];
  if (raw === undefined || raw === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

export function isPerfResult(result: CiTestResult): boolean {
  return result.extra_tags?.perf === "restore";
}

export function perfRows(results: CiTestResult[]): PerfRow[] {
  const rows = results.filter(isPerfResult).map((result) => {
    const tags = result.extra_tags ?? {};
    return {
      resultId: result.id,
      backend: tags.backend ?? "?",
      locality: tags.locality ?? null,
      n: numberTag(tags, "n") ?? 0,
      p50Ms: numberTag(tags, "p50_ms"),
      p95Ms: numberTag(tags, "p95_ms"),
      p99Ms: numberTag(tags, "p99_ms"),
      variancePct: numberTag(tags, "variance_pct"),
      tierMismatch: numberTag(tags, "tier_mismatch") ?? 0,
      status: result.status,
      error: result.error,
    };
  });
  const backendRank = (backend: string) => {
    const index = PERF_BACKEND_ORDER.indexOf(backend as (typeof PERF_BACKEND_ORDER)[number]);
    return index === -1 ? PERF_BACKEND_ORDER.length : index;
  };
  const localityRank = (locality: string | null) =>
    locality === null ? -1 : LOCALITY_ORDER.indexOf(locality as (typeof LOCALITY_ORDER)[number]);
  return rows.sort(
    (first, second) =>
      backendRank(first.backend) - backendRank(second.backend) ||
      localityRank(first.locality) - localityRank(second.locality),
  );
}

export function formatPerfMs(ms: number | null): string {
  if (ms === null) return "-";
  return ms >= 10_000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms).toLocaleString()} ms`;
}
